/**
 * Odd Field Out Props Utility
 */

import {
  FieldEntityProps,
  SelectOptions,
  FieldClassNameInterface,
  OddFieldOutPropsType,
} from '../types';

/**
 * If there's an odd number of fields, extracts the last field for full-width display.
 *
 * This is used for 2-column layouts where having an odd number of fields
 * would leave an empty space. The "odd field out" is rendered full-width
 * in its own row.
 *
 * WARNING: This function mutates the input array by popping the last element.
 *
 * @param fields - Array of field entities (will be mutated if odd length)
 * @param selectOptions - Array of select field options
 * @param dependentSelectOptions - Array of dependent select field options
 * @param selectFieldClassNames - CSS class names for select fields
 * @param textFieldClassNames - CSS class names for text fields
 * @returns Props object for the odd field, or false if no odd field
 *
 * @example
 * const oddFieldProps = getOddFieldOutProps(fields, selectOptions, ...);
 * if (oddFieldProps) {
 *   // Render full-width field
 * }
 */
export const getOddFieldOutProps = (
  fields: FieldEntityProps[],
  selectOptions: SelectOptions[],
  dependentSelectOptions: SelectOptions[],
  selectFieldClassNames: FieldClassNameInterface,
  textFieldClassNames: FieldClassNameInterface,
): OddFieldOutPropsType => {
  const isOddFields = fields.length % 2 === 1;
  const oddFieldOut = isOddFields ? fields.pop() : null;

  if (!oddFieldOut) return false;

  const fullWidthSelectClass: FieldClassNameInterface = {
    mainField: `${selectFieldClassNames.mainField} fullWidthSelectField`,
    error: selectFieldClassNames.error,
  };

  const fullWidthTextClass: FieldClassNameInterface = {
    mainField: `${textFieldClassNames.mainField} fullWidthSelectField`,
    error: textFieldClassNames.error,
  };

  switch (oddFieldOut.fieldType) {
    case 'SELECT':
      return {
        type: oddFieldOut.fieldType,
        placeholder: `${oddFieldOut.name}*`,
        field: oddFieldOut,
        selectOptions,
        selectFieldClassNames: fullWidthSelectClass,
        isTouchedAndError: false,
      };
    case 'DEPENDENT_SELECT':
      return {
        type: oddFieldOut.fieldType,
        placeholder: `${oddFieldOut.name}*`,
        field: oddFieldOut,
        dependentSelectOptions,
        selectFieldClassNames: fullWidthSelectClass,
        isTouchedAndError: false,
      };
    case 'TEXTAREA':
    case 'TEXT':
      return {
        type: oddFieldOut.fieldType,
        field: oddFieldOut,
        placeholder: `${oddFieldOut.name}*`,
        textFieldClassNames: fullWidthTextClass,
        isTouchedAndError: false,
      };
    default:
      return false;
  }
};
